import {
  Box,
  Button,
  CircularProgress,
  Container,
  Grid,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import SocialLogins from '../../components/Login/SocialLogins';
import AuthWrapper from '../../layout/auth/auth-wrapper';
import { acceptInvite, loginUser, setUserAccount } from '../../modules/users';
import { useNotification } from '../../hooks/NotificationContext';
import { addDataToLocalStorage } from '../../lib/utils';

const InvitePage = () => {
  const [status, setStatus] = useState('idle');
  const [message, setMessage] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [token, setToken] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const { addNotification } = useNotification();

  const getTokenFromURL = () => {
    const params = new URLSearchParams(location.search);
    return params.get('token');
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      const tokenData = JSON.parse(atob(token.split('.')[1]));
      const isTokenValid = Date.now() <= tokenData.exp * 1000;
      if (isTokenValid) {
        navigate('/dashboard', { replace: true });
      } else {
        localStorage.removeItem('token');
        addNotification('info', 'Please login.');
      }
    }
  }, [navigate]);

  useEffect(() => {
    const token = getTokenFromURL();
    if (token) {
      setToken(token);
    }
  }, []);

  const handleAcceptInvite = async () => {
    if (token) {
      try {
        setStatus('loading');
        const response = await acceptInvite({ token });
        setUserEmail(response.email);
        setStatus('success');
        setMessage(
          'Invitation accepted! Please choose an option to complete your setup.'
        );
      } catch (error) {
        if (error.message === 'ALREADY_ACCEPTED') {
          setStatus('ALREADY_ACCEPTED');
        } else {
          setStatus('error');
        }
      }
    } else {
      setStatus('error');
    }
  };

  const handlePasswordSetup = (values) => {
    navigate(`/reset-password?reset_password_token=${token}`, {
      state: { userEmail, isSetup: true },
    });
  };

  const handleSubmit = async (values) => {
    const user = {
      email: values.email,
      password: values.password,
      platform: values.platform,
      token: getTokenFromURL(),
    };

    try {
      await setUserAccount(user);
      const data = await loginUser(user);

      setStatus('setup-success');
      setMessage('Your account has been setup successfully! Logging you in...');
      addDataToLocalStorage(data);
      localStorage.setItem('isLoggedIn', true);
      setTimeout(() => {
        navigate('/dashboard', { replace: true });
      }, 1000);
    } catch (error) {
      setStatus('setup-error');
      setMessage(error.message);
    }
  };

  return (
    <AuthWrapper>
      <Container maxWidth="sm" sx={{ textAlign: 'center', mt: 5 }}>
        {status === 'loading' && (
          <>
            <CircularProgress />
            <Typography variant="h6" sx={{ mt: 2 }}>
              Verifying your details...
            </Typography>
          </>
        )}

        {status === 'setup-error' && (
          <>
            <Typography variant="h4" color="error" sx={{ mt: 2, mb: 1 }}>
              Oops! Something Went Wrong
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              {message}
            </Typography>
            <Grid item xs={12}>
              <Button
                disableElevation
                fullWidth
                size="large"
                variant="outlined"
                sx={{ mb: 2 }}
                onClick={handlePasswordSetup}
                color="primary">
                Create a Password
              </Button>
            </Grid>
          </>
        )}

        {status === 'setup-success' && (
          <>
            <Typography variant="h4" color="primary" sx={{ mt: 2, mb: 1 }}>
              Success!
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              {message}
            </Typography>
            {/* <Button variant="contained" onClick={() => navigate("/login")}>
              Go to Login
            </Button> */}
          </>
        )}

        {status === 'error' && (
          <>
            <Typography variant="h4" color="error" sx={{ mt: 2, mb: 1 }}>
              Oops! Something Went Wrong
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              Unfortunately, we couldn’t process your invitation. This might be
              due to an expired or invalid invite link.
            </Typography>
            <Typography variant="body2" sx={{ mb: 3 }}>
              Please ensure the link is correct and try again or contact the
              admin for a new invitation.
            </Typography>
          </>
        )}

        {status === 'ALREADY_ACCEPTED' && (
          <>
            <Typography variant="h4" color="error" sx={{ mt: 2, mb: 1 }}>
              Oops! Invitation Already Accepted
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              It seems this invitation has already been accepted. If you believe
              this is a mistake, please contact the admin for assistance.
            </Typography>
            <Link
              variant="body2"
              underline="none"
              to="/login"
              color="text.primary">
              <span>&#8592; Go to login</span>
            </Link>
          </>
        )}

        {status === 'success' && (
          <>
            <Typography variant="h4" color="primary" sx={{ mt: 2, mb: 1 }}>
              You're All Set!
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              Thank you for accepting the invitation! You can now choose how
              you'd like to set up your account.
            </Typography>

            <Grid item xs={12}>
              <Button
                disableElevation
                fullWidth
                size="large"
                variant="outlined"
                sx={{ mb: 2 }}
                onClick={handlePasswordSetup}
                color="primary">
                Create a Password
              </Button>
            </Grid>

            <SocialLogins handleSubmit={handleSubmit} />
          </>
        )}
        {status === 'idle' && (
          <>
            <Typography variant="h4" color="primary" sx={{ mt: 2, mb: 1 }}>
              You have been invited to join GAINSS!!!
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              Click the button below to accept the invite. After accepting, you
              will be redirected to next step to complete your registration.
            </Typography>
            <Box>
              <Button variant="contained" onClick={handleAcceptInvite}>
                Accept Invite
              </Button>
            </Box>
          </>
        )}
      </Container>
    </AuthWrapper>
  );
};

export default InvitePage;
